import { Component, OnInit, Input } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { tap, takeUntil } from 'rxjs/operators';

import { EFeatureFlag } from '@app/feature-toggle-list';

import { IBinLocation } from '../../../location/interfaces/location.interface';
import { TextEditorConfig } from '../../../shared/configs';
import { OptionInterface } from '../../../shared/interfaces/common.interface';
import { LocationApiService } from '../../../shared/services/api/location/location-api.service';
import { SystemSettingApiService } from '../../../shared/services/api/system-setting-api.service';
import { trackByValue } from '../../../shared/util/track-by.util';

// Configs
import { SystemSettingInterface } from '../../../system-setting/interfaces/system-setting.interface';
import { DepartmentConfig } from '../../config';

// Interfaces
import { ICategory, EProductTypes } from '../../interfaces';

// Services

@Component({
  selector: 'lum-rfq-product-information-form',
  templateUrl: './rfq-product-information-form.component.html',
  styleUrls: ['./rfq-product-information-form.component.scss'],
})
export class RfqProductInfoComponent implements OnInit {
  @Input()
  dialogData :any;

  @Input()
  form :UntypedFormGroup;

  @Input()
  productTypes :string[];

  @Input()
  productCategories :ICategory[] | null = [];

  @Input()
  productTags :OptionInterface[] = [];

  @Input()
  unitClasses :any = [];

  @Input()
  unitMeasures :any = [];

  @Input()
  factories :any = [];

  @Input()
  defaultWarehouseLocation :any = [];

  textEditorConfig = TextEditorConfig;
  quote_expires :boolean = false;
  departments = DepartmentConfig.productDepartments;
  selectedTags :OptionInterface[] = [];
  settings :any = [];
  binLocations :IBinLocation[] = [];

  settings$ :Observable<SystemSettingInterface[]>;
  defaultWarehouseLocations$ :Observable<IBinLocation>;
  binLocations$ :Observable<IBinLocation[]>;
  featureFlag = EFeatureFlag;
  trackByValue = trackByValue;

  private onDestroy$ = new Subject();

  constructor(
    private fb :UntypedFormBuilder,
    private locationApiService :LocationApiService,
    private systemSettingApiService :SystemSettingApiService,
  ) {}

  ngOnInit() :void {
    this.initDefaults();
    this.getDataFromService();
    this.getSettings().subscribe();
    this.getWarehouse().subscribe();
    this.getBinLocations().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onSetTags(tags :any) :void {
    this.form.patchValue({ product_tags: tags?.tag });
  }

  onAddLink(link ?:any) :void {
    const links = this.form.get('product_links') as UntypedFormArray;
    const linkObj :any = this.fb.group({
      lv: [link ? link.lv : ''],
    });
    links.push(linkObj);
  }

  onRemoveLink(idx :number) :void {
    const links = this.form.get('product_links') as UntypedFormArray;
    links.removeAt(idx);
  }

  onAddMaterial(mat ?:any) :void {
    const mats = this.form.get('material') as UntypedFormArray;
    const materialFc :any = this.fb.group({
      mv: [mat ? mat.mv : ''],
    });
    mats.push(materialFc);
  }

  onRemoveMaterial(idx :number) :void {
    const mats = this.form.get('material') as UntypedFormArray;
    mats.removeAt(idx);
  }

  onChangeType() :void {
    if (this.isKit) {
      this.form.get('unit_class_id')?.clearValidators();
      this.form.get('default_selling_unit_id')?.clearValidators();
      this.form.get('default_purchase_unit_id')?.clearValidators();
    } else {
      this.form.get('unit_class_id')?.setValidators(Validators.required);
      this.form.get('default_selling_unit_id')?.setValidators(Validators.required);
      this.form.get('default_purchase_unit_id')?.setValidators(Validators.required);
    }
  }

  private initDefaults() :void {
    this.onAddLink();
    this.onAddMaterial();
  }

  private getDataFromService() :void {
    this.settings$ = this.systemSettingApiService.settings$;
    this.defaultWarehouseLocations$ = this.locationApiService.warehouse$;
    this.binLocations$ = this.locationApiService.binLocations$;
  }

  private getSettings() :Observable<SystemSettingInterface[]> {
    return this.settings$.pipe(
      tap((settings :SystemSettingInterface[]) => {
        if (settings) {
          settings.forEach((row :any) => {
            try {
              this.settings[row.key] = JSON.parse(row.value);
            } catch (e) {}
          });
          if (this.settings.inventory_settings) {
            this.locationApiService.getWarehouseById(
              this.settings?.inventory_settings?.warehouse_id,
            );
          }
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getWarehouse() :Observable<IBinLocation> {
    return this.defaultWarehouseLocations$.pipe(
      tap((warehouse :IBinLocation) => {
        if (warehouse) {
          this.locationApiService.getAll({
            all: 1,
            parent_id: warehouse.id,
            defaultReceivingBinId: warehouse.default_receiving_location,
          });

          this.form.patchValue({
            default_receiving_bin_id: warehouse.default_receiving_location,
          });
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private getBinLocations() :Observable<IBinLocation[]> {
    return this.binLocations$.pipe(
      tap((binLocations :IBinLocation[]) => {
        if (binLocations) {
          this.binLocations = binLocations;
        }
      }),
      takeUntil(this.onDestroy$),
    );
  }

  get productLinksControl() :UntypedFormArray {
    return this.form.get('product_links') as UntypedFormArray;
  }

  get productNameControl() :UntypedFormControl {
    return this.form.get('product_name') as UntypedFormControl;
  }

  get internalSkuControl() :UntypedFormControl {
    return this.form.get('internal_sku') as UntypedFormControl;
  }

  get productCategoryControl() :UntypedFormControl {
    return this.form.get('product_category_id') as UntypedFormControl;
  }

  get unitClassIdControl() :UntypedFormControl {
    return this.form.get('unit_class_id') as UntypedFormControl;
  }

  get defaultSellingUnitIdControl() :UntypedFormControl {
    return this.form.get('default_selling_unit_id') as UntypedFormControl;
  }

  get defaultPurchaseUnitIdControl() :UntypedFormControl {
    return this.form.get('default_purchase_unit_id') as UntypedFormControl;
  }

  get materialsControl() :UntypedFormArray {
    return this.form.get('material') as UntypedFormArray;
  }

  get isKit() :boolean {
    return this.form.get('type')?.value === EProductTypes.KIT;
  }

  get newKitsStructureEnabled() :boolean {
    return this.dialogData?.featureFlag?.newKitsStructureEnabled && this.isKit;
  }

  get lotTrackingEnabled() :boolean {
    return this.dialogData?.featureFlag?.lotTrackingEnabled && !this.isKit;
  }
}
